import { DateTime } from 'luxon';
import { GOAL_CYCLE, GOAL_PLANNING, GOAL_RELATION, GOAL_SELF } from '@/lib/props/custom-types';
import { dateScopeDynamicType, dateScopeType, propertyType, userScopeOperator, userScopeType } from 'shared/constants.json';
import { isEmpty, isNullOrUndefined } from 'shared/lib/object/object';
import { isEmptyRange as isEmptyDateRange, isEmptyType as isEmptyDateType, isNotEmptyType as isNotEmptyDateType } from '@/lib/filter/dynamic-date';
import { isEmptyRange as isEmptyNumberRange } from '@/lib/filter/number';
import { isNotANumber } from 'shared/lib/number';

export const AND = userScopeOperator.and;
export const OR = userScopeOperator.or;
export const NOT = userScopeOperator.not;
export const EMPTY = 'empty';
export const NOT_EMPTY = 'not_empty';

const isValidOperator = (operator) => {
  const allowed = [AND, OR, NOT];
  return allowed.includes(operator);
};

const hasUid = (object) => object !== null && object.uid !== undefined;

const hasValidDirectProperty = (scope) => !isNullOrUndefined(scope.directProperty)
  && !isNullOrUndefined(scope.directProperty.edgeName)
  && !isNullOrUndefined(scope.directProperty.type);

const hasValidProperty = (scope) => scope.property !== undefined
  && scope.property !== null
  && hasUid(scope.property)
  && scope.property.type !== undefined;

const hasValidStaticUsers = (scope) => !isNullOrUndefined(scope.staticUsers);

const isValidStaticUsers = (scope) => hasValidStaticUsers(scope) && Array.isArray(scope.staticUsers);

export const isValidTimeRange = (timeRange) => {
  if (isNullOrUndefined(timeRange)) {
    return false;
  }

  if (!dateScopeDynamicType.all.includes(timeRange.dynamicType)
    || !dateScopeType.all.includes(timeRange.type)) {
    return false;
  }

  if (!isNullOrUndefined(timeRange.start) && !DateTime.fromISO(timeRange.start).isValid) {
    return false;
  }

  if (!isNullOrUndefined(timeRange.end) && !DateTime.fromISO(timeRange.end).isValid) {
    return false;
  }

  return true;
};

export const isValidNumberRange = (numberRange) => {
  if (isNullOrUndefined(numberRange)) {
    return false;
  }

  if (numberRange.minType !== undefined) {
    return isNullOrUndefined(numberRange.min) || !isNotANumber(numberRange.min);
  }

  if (numberRange.maxType !== undefined) {
    return isNullOrUndefined(numberRange.max) || !isNotANumber(numberRange.max);
  }

  return false;
};

const isValidDateScope = (scope) => hasValidProperty(scope) && scope.property.type === propertyType.date && isValidTimeRange(scope.timeRange);

const isValidDirectDateScope = (scope) => hasValidDirectProperty(scope) && scope.directProperty.type === propertyType.date && isValidTimeRange(scope.timeRange);

const isValidNumberScope = (scope) => hasValidProperty(scope) && scope.property.type === propertyType.number && isValidNumberRange(scope.numberRange);

const isValidDirectNumberScope = (scope) => hasValidDirectProperty(scope) && scope.directProperty.type === propertyType.number && isValidNumberRange(scope.numberRange);

const isValidSpaceScope = (scope) => hasValidProperty(scope) && scope.property.type === propertyType.space && Array.isArray(scope.spaces);

const isValidDirectSpaceScope = (scope) => hasValidDirectProperty(scope) && scope.directProperty.type === propertyType.space && Array.isArray(scope.spaces);

const isValidOptionScope = (scope) => hasValidProperty(scope)
  && [propertyType.options, propertyType.singleSelect, propertyType.status].includes(scope.property.type)
  && Array.isArray(scope.selectedOptions);

const isValidDirectOptionScope = (scope) => hasValidDirectProperty(scope) && [propertyType.options, propertyType.singleSelect, propertyType.status].includes(scope.directProperty.type) && Array.isArray(scope.selectedOptions);

const isValidUsersScope = (scope) => hasValidProperty(scope) && scope.property.type === propertyType.user && Array.isArray(scope.users);

const isValidDirectUsersScope = (scope) => hasValidDirectProperty(scope) && scope.directProperty.type === propertyType.user && Array.isArray(scope.users);

const isValidDirectGoalScope = (scope) => hasValidDirectProperty(scope)
  && [GOAL_RELATION, GOAL_SELF].includes(scope.directProperty.type)
  && Array.isArray(scope.relations);

const isValidLookupScope = (scope) => hasValidProperty(scope)
  && scope.property.type === propertyType.lookup
  && (
    (
      scope.property.lookupProperty.type === propertyType.user
      && Array.isArray(scope.users)
    ) || (
      [propertyType.options, propertyType.singleSelect].includes(scope.property.lookupProperty.type)
      && Array.isArray(scope.selectedOptions)
    ) || (
      scope.property.lookupProperty.type === propertyType.space
      && Array.isArray(scope.spaces)
    )
  );

export const isValidScope = (scope) => {
  if (isNullOrUndefined(scope)) {
    return false;
  }

  return (
    isValidStaticUsers(scope)
    || isValidDateScope(scope)
    || isValidNumberScope(scope)
    || isValidSpaceScope(scope)
    || isValidOptionScope(scope)
    || isValidUsersScope(scope)
    || isValidDirectDateScope(scope)
    || isValidDirectNumberScope(scope)
    || isValidDirectSpaceScope(scope)
    || isValidDirectOptionScope(scope)
    || isValidDirectUsersScope(scope)
    || isValidDirectGoalScope(scope)
    || isValidLookupScope(scope)
  );
};

const collectScopeSignatures = (node, sigs) => {
  const { scope } = node;
  if (!isNullOrUndefined(scope)) {
    if (!isNullOrUndefined(scope.property) && !isNullOrUndefined(scope.property.uid)) {
      sigs.add(`property:${scope.property.uid}`);
    } else if (!isNullOrUndefined(scope.directProperty) && !isNullOrUndefined(scope.directProperty.edgeName)) {
      sigs.add(`direct:${scope.directProperty.edgeName}`);
    } else if (!isNullOrUndefined(scope.staticUsers)) {
      sigs.add('staticUsers');
    } else {
      sigs.add(`other:${scope.type}`);
    }
  }
  if (Array.isArray(node.children)) {
    node.children.forEach((child) => collectScopeSignatures(child, sigs));
  }
};

// A quick filter must reference exactly one property; >= 2 distinct scopes is an advanced filter.
const spansMultipleScopes = (tree) => {
  const sigs = new Set();
  collectScopeSignatures(tree, sigs);
  return sigs.size >= 2;
};

const isValidChildTree = (tree, minDepth, maxDepth, currentDepth = 0, quickFilters = []) => {
  if (currentDepth > maxDepth) {
    return false;
  }
  if (tree === null) {
    return true;
  }

  if (quickFilters.includes(tree.key)) {
    if (spansMultipleScopes(tree)) {
      return false;
    }
    maxDepth = 3;
    minDepth = 3;
  }

  if (!Array.isArray(tree.children) || tree.children.length === 0) {
    if (currentDepth < minDepth) {
      return false;
    }
    return isValidScope(tree.scope);
  }

  if (!isValidOperator(tree.op)) {
    return false;
  }

  return tree.children.every((next) => isValidChildTree(next, minDepth, maxDepth, currentDepth + 1, quickFilters));
};

export const isValidTree = (tree, { minDepth = 0, maxDepth = 5, quickFilters = [] } = { minDepth: 0, maxDepth: 5, quickFilters: [] }) => {
  if (tree === undefined) {
    return false;
  }
  if (tree === null) {
    return true;
  }
  if (isNullOrUndefined(tree.account) || !hasUid(tree.account)) {
    return false;
  }
  if ((!Array.isArray(tree.children) || tree.children.length === 0)
    && isNullOrUndefined(tree.scope)) {
    return true;
  }
  return isValidChildTree(tree, minDepth, maxDepth, 0, quickFilters);
};

export const inferScopeType = (scope) => {
  if (scope.type !== undefined) {
    if (userScopeType.all.includes(scope.type)) {
      return scope.type;
    }
    return undefined;
  }

  if (!isNullOrUndefined(scope.staticUsers)) {
    return userScopeType.staticUsers;
  }
  if (!isNullOrUndefined(scope.reverseProperty)) {
    return userScopeType.reverseProperty;
  }
  if (!isNullOrUndefined(scope.indirectProperty)) {
    return userScopeType.indirectProperty;
  }
  if (!isNullOrUndefined(scope.directProperty)) {
    return userScopeType.directProperty;
  }
  if (!isNullOrUndefined(scope.property)) {
    return userScopeType.property;
  }
  return undefined;
};

const hasValueText = (scope) => isEmpty(scope.text);
const hasValueDate = (scope) => isEmptyDateType(scope.timeRange) || isNotEmptyDateType(scope.timeRange) || !isEmptyDateRange(scope.timeRange);
const hasValueNumber = (scope) => !isEmptyNumberRange(scope.numberRange);
const hasValueUsers = (scope) => scope.users.length > 0;
const hasValueSpaces = (scope) => scope.spaces.length > 0;
const hasValueOptions = (scope) => scope.selectedOptions.length > 0;
const hasValueRelations = (scope) => Array.isArray(scope.relations) && scope.relations.length > 0;
const hasValuePlannings = (scope) => scope.plannings.length > 0;
const hasValueGoalCycles = (scope) => Array.isArray(scope.goalCycles) && scope.goalCycles.length > 0;

const hasValueScopeProperty = (scope, scopePropertyType) => {
  switch (scopePropertyType) {
    case propertyType.text:
    case propertyType.url:
    case propertyType.richText:
      return hasValueText(scope);
    case propertyType.date:
      return hasValueDate(scope);
    case propertyType.number:
      return hasValueNumber(scope);
    case propertyType.user:
      return hasValueUsers(scope);
    case propertyType.space:
      return hasValueSpaces(scope);
    case propertyType.status:
    case propertyType.options:
    case propertyType.singleSelect:
      return hasValueOptions(scope);
    case propertyType.lookup:
      return (hasValueUsers(scope) && scope.property.lookupProperty.type === propertyType.user)
        || (hasValueSpaces(scope) && scope.property.lookupProperty.type === propertyType.space)
        || (hasValueOptions(scope) && [propertyType.options, propertyType.singleSelect].includes(scope.property.lookupProperty.type));
    case GOAL_RELATION:
    case GOAL_SELF:
      return hasValueRelations(scope);
    case GOAL_PLANNING:
      return hasValuePlannings(scope);
    case GOAL_CYCLE:
      return hasValueGoalCycles(scope);

    default:
      return false;
  }
};

export const hasValueScope = (scope) => {
  if (isNullOrUndefined(scope)) {
    return false;
  }

  if (scope.isEmpty) {
    return true;
  }

  switch (inferScopeType(scope)) {
    case userScopeType.staticUsers:
      return scope.staticUsers.length > 0;
    case userScopeType.directProperty:
      return hasValueScopeProperty(scope, scope.directProperty.type);
    case userScopeType.property:
      return hasValueScopeProperty(scope, scope.property.type);
    case userScopeType.indirectProperty:
      return hasValueScope(scope.indirectProperty.scope);
    case userScopeType.reverseProperty:
      return true;
    default:
      return false;
  }
};

export const treeHasValue = (tree) => {
  if (isNullOrUndefined(tree)) {
    return false;
  }
  if (!isNullOrUndefined(tree.scope) && hasValueScope(tree.scope)) {
    return true;
  }
  if (Array.isArray(tree.children)) {
    return tree.children.some((child) => treeHasValue(child));
  }
  return false;
};
